import React, { useState, Component } from 'react';
import { Button } from '../ui/button';
import { Copy, Share2, Twitter, Facebook, MessageCircle, Link as LinkIcon, Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getPublicTranscriptPageUrl } from '../../lib/api';
interface ShareTranscriptProps {
  taskId: string;
  title?: string;
  className?: string;
  variant?: 'default' | 'compact';
}
export function ShareTranscript({
  taskId,
  title = 'TikTok Transcript',
  className,
  variant = 'default'
}: ShareTranscriptProps) {
  const [copied, setCopied] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const shareUrl = getPublicTranscriptPageUrl(taskId, 'app');
  const encodedTitle = encodeURIComponent(title);
  const encodedUrl = encodeURIComponent(shareUrl);
  const shareText = `Check out this transcript from ${title} via ScribeTok`;
  const encodedShareText = encodeURIComponent(shareText);
  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const toggleShareOptions = () => {
    setShowShareOptions(!showShareOptions);
  };
  // Share to Twitter
  const shareToTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodedShareText}&url=${encodedUrl}`, '_blank');
  };
  // Share to Facebook
  const shareToFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, '_blank');
  };
  // Share via SMS
  const shareViaSms = () => {
    window.open(`sms:?body=${encodedShareText} ${encodedUrl}`, '_blank');
  };
  // Share via WhatsApp
  const shareViaWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodedShareText} ${encodedUrl}`, '_blank');
  };
  if (variant === 'compact') {
    return <div className={cn('relative', className)} data-id="element-1594">
        <Button variant="outline" size="sm" className="gap-2" onClick={toggleShareOptions} data-id="element-1595">
          <Share2 size={16} data-id="element-1596" />
          Share
        </Button>
        {showShareOptions && <div className="absolute top-full right-0 mt-2 p-3 bg-card rounded-lg border border-border shadow-lg z-10 w-64" data-id="element-1597">
            <div className="grid grid-cols-2 gap-2" data-id="element-1598">
              <Button variant="ghost" size="sm" className="justify-start" onClick={handleCopyLink} data-id="element-1599">
                {copied ? <Check size={14} className="mr-2" data-id="element-1600" /> : <Copy size={14} className="mr-2" data-id="element-1601" />}
                Copy Link
              </Button>
              <Button variant="ghost" size="sm" className="justify-start" onClick={shareToTwitter} data-id="element-1602">
                <Twitter size={14} className="mr-2" data-id="element-1603" />
                Twitter
              </Button>
              <Button variant="ghost" size="sm" className="justify-start" onClick={shareToFacebook} data-id="element-1604">
                <Facebook size={14} className="mr-2" data-id="element-1605" />
                Facebook
              </Button>
              <Button variant="ghost" size="sm" className="justify-start" onClick={shareViaSms} data-id="element-1606">
                <MessageCircle size={14} className="mr-2" data-id="element-1607" />
                SMS
              </Button>
            </div>
            <Button variant="ghost" size="sm" className="w-full justify-start mt-2" onClick={shareViaWhatsApp} data-id="element-1608">
              <span className="mr-2" data-id="element-1609">📱</span>
              WhatsApp
            </Button>
          </div>}
      </div>;
  }
  return <div className={cn('space-y-4', className)} data-id="element-1610">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1611">
        <h3 className="font-semibold text-base flex items-center gap-2" data-id="element-1612">
          <Share2 size={18} className="text-primary" data-id="element-1613" />
          Share This Transcript
        </h3>
        <div className="text-xs bg-primary/10 text-primary px-2.5 py-1 rounded-full whitespace-nowrap" data-id="element-1614">
          Share with friends
        </div>
      </div>

      {/* Copy Link Input */}
      <div className="flex gap-2" data-id="element-1615">
        <input type="text" value={shareUrl} readOnly className="flex-1 min-w-0 px-3 py-2.5 rounded-lg bg-secondary/50 border border-border focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm truncate" onClick={e => e.currentTarget.select()} data-id="element-1616" />
        <Button variant="default" size="sm" onClick={handleCopyLink} className="flex-shrink-0 h-auto px-4" data-id="element-1617">
          {copied ? <>
              <Check size={16} data-id="element-1618" />
              <span className="ml-2 hidden sm:inline" data-id="element-1619">Copied!</span>
            </> : <>
              <Copy size={16} data-id="element-1620" />
              <span className="ml-2 hidden sm:inline" data-id="element-1621">Copy</span>
            </>}
        </Button>
      </div>

      {/* Share Buttons - Responsive Grid */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3" data-id="element-1622">
        <Button variant="outline" size="sm" className="w-full justify-center h-11" onClick={shareToTwitter} data-id="element-1623">
          <Twitter size={16} className="sm:mr-2" data-id="element-1624" />
          <span className="hidden sm:inline" data-id="element-1625">Twitter</span>
        </Button>
        <Button variant="outline" size="sm" className="w-full justify-center h-11" onClick={shareToFacebook} data-id="element-1626">
          <Facebook size={16} className="sm:mr-2" data-id="element-1627" />
          <span className="hidden sm:inline" data-id="element-1628">Facebook</span>
        </Button>
        <Button variant="outline" size="sm" className="w-full justify-center h-11" onClick={shareViaSms} data-id="element-1629">
          <MessageCircle size={16} className="sm:mr-2" data-id="element-1630" />
          <span className="hidden sm:inline" data-id="element-1631">SMS</span>
        </Button>
        <Button variant="outline" size="sm" className="w-full justify-center h-11" onClick={shareViaWhatsApp} data-id="element-1632">
          <span className="text-base sm:mr-2" data-id="element-1633">📱</span>
          <span className="hidden sm:inline" data-id="element-1634">WhatsApp</span>
        </Button>
      </div>

      {/* Helper Text */}
      <div className="text-xs text-muted-foreground flex items-center justify-center gap-1.5 pt-1" data-id="element-1635">
        <LinkIcon size={12} data-id="element-1636" />
        <span data-id="element-1637">Help friends save time with this transcript</span>
      </div>
    </div>;
}